import { useEffect, useMemo } from 'react';
import { Form } from 'semantic-ui-react';
import { useMutation } from 'react-query';
import { getInvestors } from '~/api/admin';
import { FormSchemaAwareWidget } from '~/components/form';
import { Project, ProjectInvestorQueue } from '~/types/project';
import { useProjectContext, usePortfolioContext, usePageType } from '~/hooks';
import { useParams } from 'react-router-dom';
import './styles.scss';

interface InvestorInvitationProps {
  investorRow?: ProjectInvestorQueue;
}

const InvestorInvitationForm = ({  
  investorRow,
}: InvestorInvitationProps ) => {
  const pageType = usePageType();
  const { id } = useParams<{ id: string }>();
  const { project } = useProjectContext();
  const { portfolio } = usePortfolioContext();
  const parentProject = portfolio?.projects?.find((project: Project) => !project.parentId);
  const investorQueue = pageType === 'project' ? project?.investorQueue : parentProject?.investorQueue;
  const preferredInvestors = pageType === 'project' ? project?.preferredInvestors : portfolio?.preferredInvestors;
  
  const { mutate: fetchInvestors, data: allInvestors = [] } = useMutation(
    async () => {
      if (!investorQueue) {
        return [];
      }
      if (pageType === 'project') {
        return getInvestors({ projectId: id });
      }
      if (pageType === 'portfolio') {
        return getInvestors({ portfolioId: id });
      }
      return getInvestors();
    }
  );

  // Trigger fetch on mount
  useEffect(() => {
    fetchInvestors(); 
  }, []);

  const possibleInvestors = useMemo(() => {
    if (!allInvestors) return [];

    const allPossible = allInvestors
      .filter((investor: any) => 
        !investorQueue?.map((iq: any) => iq.investorId).includes(investor.id)
      )
      .map((investor: any) => {
        const flags: string[] = [];
        if (investor.isExcludedForDeal) {
          flags.push('Excluded');
        }
        if (investor.hasRestrictedAccess) {
          flags.push('Restricted access');
        }
        return {
          key: String(investor.id),
          text: flags.length > 0
            ? `${investor.company.name} (${flags.join(', ')})`
            : investor.company.name,
          value: String(investor.id),
        };
      });

    if (!preferredInvestors?.length) {
      return allPossible;
    }

    const preferred = allPossible
      .filter((investor: any) => 
        preferredInvestors.some((pInvestor: any) => investor.key === pInvestor.id)
      )
      .map((investor: any) => ({
        ...investor,
        text: `*** ${investor.text}`
      }));

    const others = allPossible.filter((investor: any) => 
      !preferredInvestors.some((pInvestor: any) => investor.key === pInvestor.id)
    );

    return [...preferred, ...others];
  }, [investorQueue, allInvestors, preferredInvestors]);

  return (
   <Form className="invitation-form">
      {investorRow ? (
        <FormSchemaAwareWidget
          schemaKey="investorName"
          fieldLabel="Investor"
          disabled
        />
      ) : (
        <>
          <FormSchemaAwareWidget
            schemaKey="investorId"
            fieldLabel="Investor"
            options={possibleInvestors}
            search
            placeholder='Choose one...'
          />
        </>
      )}
    </Form>
  );
};

export default InvestorInvitationForm;
